/**
 * Color palettes and themes for the mind map application
 */

// Original concept palette retained so existing map gradients can be
// interpreted by their historical picker position without rewriting data.
export const LEGACY_COLORS = [
  // Blues & Cyans
  { name: "Skyglint", gradient: "linear-gradient(135deg, #0056b3 0%, #003d82 100%)" },
  { name: "Seraphim Blue", gradient: "linear-gradient(135deg, #0080cc 0%, #2b5ce6 100%)" },
  { name: "Frostwink", gradient: "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)" },
  { name: "Oceancloud", gradient: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)" },
  { name: "Deep Sea", gradient: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" },

  // Purples & Violets
  { name: "Dreamburst", gradient: "linear-gradient(135deg, #4527a0 0%, #512da8 100%)" },
  { name: "Nightfire", gradient: "linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)" },
  { name: "Electric Haze", gradient: "linear-gradient(135deg, #ad1457 0%, #4a148c 100%)" },
  { name: "Lavashock", gradient: "linear-gradient(135deg, #8e24aa 0%, #5e35b1 100%)" },
  { name: "Mystdust", gradient: "linear-gradient(135deg, #7b1fa2 0%, #3f51b5 100%)" },

  // Pinks & Corals
  { name: "Coralite", gradient: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)" },
  { name: "Cherrywave", gradient: "linear-gradient(135deg, #d84315 0%, #c2185b 100%)" },
  { name: "Crimson Sky", gradient: "linear-gradient(135deg, #d32f2f 0%, #f57c00 100%)" },
  { name: "Rosegold", gradient: "linear-gradient(135deg, #ad1457 0%, #e53935 100%)" },
  { name: "Blushfire", gradient: "linear-gradient(135deg, #c2185b 0%, #e91e63 100%)" },

  // Oranges & Yellows
  { name: "Sunblush", gradient: "linear-gradient(135deg, #ef6c00 0%, #d84315 100%)" },
  { name: "Peachfuzz", gradient: "linear-gradient(135deg, #ff8f00 0%, #f57c00 100%)" },
  { name: "Amberfade", gradient: "linear-gradient(135deg, #ff8f00 0%, #e65100 100%)" },
  { name: "Goldflare", gradient: "linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)" },
  { name: "Honeywave", gradient: "linear-gradient(135deg, #e65100 0%, #ff6f00 100%)" },

  // Greens & Teals
  { name: "Lushbeam", gradient: "linear-gradient(135deg, #2e7d32 0%, #00695c 100%)" },
  { name: "Mossglow", gradient: "linear-gradient(135deg, #388e3c 0%, #1976d2 100%)" },
  { name: "Meadowmint", gradient: "linear-gradient(135deg, #00695c 0%, #4527a0 100%)" },
  { name: "Forestmist", gradient: "linear-gradient(135deg, #1b5e20 0%, #00695c 100%)" },
  { name: "Stormbliss", gradient: "linear-gradient(135deg, #424242 0%, #212121 100%)" }
];

// Canonical 5 × 5 palette. Slots are deliberately ordered by hue family and
// remain stable across every map theme. All tones are dark enough to retain
// readable white text in the default theme; lighter themes provide dark text.
export const COLORS = [
  // Blues, cyans & teals
  { name: "Cobalt", gradient: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" },
  { name: "Azure", gradient: "linear-gradient(135deg, #0369a1 0%, #075985 100%)" },
  { name: "Cyan", gradient: "linear-gradient(135deg, #0e7490 0%, #155e75 100%)" },
  { name: "Teal", gradient: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)" },
  { name: "Navy", gradient: "linear-gradient(135deg, #3730a3 0%, #312e81 100%)" },

  // Indigos, violets & purples
  { name: "Indigo", gradient: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)" },
  { name: "Violet", gradient: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)" },
  { name: "Purple", gradient: "linear-gradient(135deg, #7e22ce 0%, #6b21a8 100%)" },
  { name: "Orchid", gradient: "linear-gradient(135deg, #a21caf 0%, #86198f 100%)" },
  { name: "Plum", gradient: "linear-gradient(135deg, #86198f 0%, #701a75 100%)" },

  // Pinks, reds & earth reds
  { name: "Pink", gradient: "linear-gradient(135deg, #be185d 0%, #9d174d 100%)" },
  { name: "Rose", gradient: "linear-gradient(135deg, #be123c 0%, #9f1239 100%)" },
  { name: "Red", gradient: "linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)" },
  { name: "Coral", gradient: "linear-gradient(135deg, #c2415d 0%, #9f334d 100%)" },
  { name: "Terracotta", gradient: "linear-gradient(135deg, #b5451b 0%, #8f3618 100%)" },

  // Oranges, ambers & browns
  { name: "Orange", gradient: "linear-gradient(135deg, #c2410c 0%, #9a3412 100%)" },
  { name: "Tangerine", gradient: "linear-gradient(135deg, #b45309 0%, #92400e 100%)" },
  { name: "Amber", gradient: "linear-gradient(135deg, #a16207 0%, #854d0e 100%)" },
  { name: "Ochre", gradient: "linear-gradient(135deg, #8f5f0a 0%, #713f12 100%)" },
  { name: "Brown", gradient: "linear-gradient(135deg, #854d2b 0%, #663c23 100%)" },

  // Greens & neutral
  { name: "Green", gradient: "linear-gradient(135deg, #15803d 0%, #166534 100%)" },
  { name: "Emerald", gradient: "linear-gradient(135deg, #047857 0%, #065f46 100%)" },
  { name: "Leaf", gradient: "linear-gradient(135deg, #3f7d20 0%, #35651c 100%)" },
  { name: "Olive", gradient: "linear-gradient(135deg, #4d7c0f 0%, #3f6212 100%)" },
  { name: "Slate", gradient: "linear-gradient(135deg, #475569 0%, #334155 100%)" }
];

// Utility functions for color manipulation
export const ColorUtils = {
  /**
   * Get a random canonical (or explicitly requested legacy) color.
   * @param {string} paletteName - Palette name (default: 'default')
   * @returns {Object} Color object with name and gradient
   */
  getRandomColor(paletteName = 'default') {
    const palette = paletteName === 'legacy' ? LEGACY_COLORS : COLORS;
    const randomIndex = Math.floor(Math.random() * palette.length);
    return palette[randomIndex];
  },

  /**
   * Get a current or historical color by name.
   * @param {string} colorName - Name of the color
   * @returns {Object|null} Color object or null if not found
   */
  getColorByName(colorName) {
    return [...COLORS, ...LEGACY_COLORS].find((color) => color.name === colorName) || null;
  },

  /**
   * Get contrasting text color for a background
   * @param {string} backgroundGradient - CSS gradient string
   * @returns {string} Either 'white' or 'black'
   */
  getContrastingTextColor(backgroundGradient) {
    // Extract first color from gradient for luminance calculation
    const colorMatch = backgroundGradient.match(/#[0-9a-fA-F]{6}/);
    if (!colorMatch) return 'white'; // Default to white if can't parse

    const hex = colorMatch[0];
    const luminance = this.getLuminance(hex);

    // Use white text for dark backgrounds, black for light
    return luminance > 0.5 ? 'black' : 'white';
  },

  /**
   * Calculate luminance of a hex color
   * @param {string} hex - Hex color string (e.g., '#ff0000')
   * @returns {number} Luminance value (0-1)
   */
  getLuminance(hex) {
    const r = parseInt(hex.substr(1, 2), 16) / 255;
    const g = parseInt(hex.substr(3, 2), 16) / 255;
    const b = parseInt(hex.substr(5, 2), 16) / 255;

    const [rs, gs, bs] = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Generate a gradient variant of an existing color
   * @param {string} baseColor - Base hex color
   * @param {number} lightnessAdjustment - Adjustment factor (-0.3 to 0.3)
   * @returns {string} CSS gradient string
   */
  generateGradientVariant(baseColor, lightnessAdjustment = 0.2) {
    // Simple implementation - in production, use a proper color library
    const darker = this.adjustBrightness(baseColor, -Math.abs(lightnessAdjustment));
    //const lighter = this.adjustBrightness(baseColor, Math.abs(lightnessAdjustment));

    return `linear-gradient(135deg, ${baseColor} 0%, ${darker} 100%)`;
  },

  /**
   * Adjust brightness of a hex color
   * @param {string} hex - Hex color string
   * @param {number} factor - Adjustment factor (-1 to 1)
   * @returns {string} Adjusted hex color
   */
  adjustBrightness(hex, factor) {
    const r = Math.max(0, Math.min(255, parseInt(hex.substr(1, 2), 16) + factor * 255));
    const g = Math.max(0, Math.min(255, parseInt(hex.substr(3, 2), 16) + factor * 255));
    const b = Math.max(0, Math.min(255, parseInt(hex.substr(5, 2), 16) + factor * 255));

    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  }
};

// Export default color set for backward compatibility
export { COLORS as default };
